import { useNavigate, useSearch } from '@tanstack/react-router';
import { Button } from '@sara/ui/components/button';
import { useDialog } from '@sara/ui/hooks/use-dialog';
import { Plus } from 'lucide-react';
import { useActiveOrganization } from '../../contexts/auth';
import { CreateInterviewDialog, InterviewListContainer } from '../../contexts/interviews';
import type { InterviewInviteListItemDto } from '@sara/api-client';
import { interviewsSearchSchema, type InterviewsSearch } from '../../router/tree';
import { PageHeader } from '../../components/page-header';

export function InterviewsPage() {
  const { organization } = useActiveOrganization();
  const createDialog = useDialog(CreateInterviewDialog);
  const search = useSearch({ from: '/authenticated/orgs/$orgSlug/sessions' });
  const navigate = useNavigate();

  return (
    <div className="flex min-h-[640px] flex-1 flex-col gap-12">
      <PageHeader
        title="Sessions"
        description="Pending invites, live conversations, and completed results — every interview your team has run. Send a new invite to start the next one."
        action={
          organization && (
            <Button
              variant="tinted"
              shape="pill"
              size="sm"
              className="h-8 gap-1.5 px-3 text-xs [&_svg]:size-3.5 sm:h-9 sm:px-4 sm:text-sm sm:[&_svg]:size-4"
              onClick={() => createDialog.open({ orgId: organization.id })}
            >
              <Plus strokeWidth={1.6} />
              New interview
            </Button>
          )
        }
      />

      <section className="flex min-h-0 flex-1 flex-col">
        {organization && (
          <InterviewListContainer
            orgId={organization.id}
            search={search}
            onSearchChange={(update: Partial<InterviewsSearch>) =>
              void navigate({
                to: '/orgs/$orgSlug/sessions',
                params: { orgSlug: organization.slug },
                search: (prev) =>
                  interviewsSearchSchema.parse({ ...prev, ...update }) as InterviewsSearch,
              })
            }
            onRowClick={(invite: InterviewInviteListItemDto) =>
              void navigate({
                to: '/orgs/$orgSlug/sessions/$interviewId',
                params: { orgSlug: organization.slug, interviewId: invite.id },
              })
            }
          />
        )}
      </section>
    </div>
  );
}
